import { find } from "lodash"

import ChairsWithFutureAssignments from "./ChairsWithFutureAssignments"
import {
	isSmallChairPatient,
	getEligibleChairsForInfusionType
} from "../../utils/realTimeChairUtils"
import {
	NoChairFoundForPatientError,
	ChairNotFoundError,
	PatientNotFoundError
} from "./errors"
import reassignPatients from "./reassignPatients"

/**
 * Chair assignment for a "non-small-chair" patient — patient can sit anywhere.
 * Picks the first chair with no overlapping assignments.
 */
const getChairForNonSmallChairPatient = (
	patient,
	chairsWithFutureAssignments,
	sandbox,
	isTomorrowMode
) => {
	const chairIds = chairsWithFutureAssignments.getChairAvailabilityForPatient(
		patient,
		sandbox,
		isTomorrowMode
	)
	if (chairIds.length === 0) {
		throw new NoChairFoundForPatientError(
			`No chairs available for (non small chair) patient with id ${patient.patientId}`,
			patient.patientId
		)
	}
	return { chairId: chairIds[0], otherPatientsToReassign: [] }
}

/**
 * Chair assignment for a "small-chair" patient — patient is restricted to a
 * small set of eligible chairs by infusion type. May bump non-ongoing,
 * non-small-chair patients off chairs.
 */
const getChairForSmallChairPatient = (
	patient,
	chairsWithFutureAssignments,
	sandbox,
	isTomorrowMode
) => {
	const schedule = chairsWithFutureAssignments.schedule
	const acuityTable = chairsWithFutureAssignments.acuityTable
	const eligibleChairs = getEligibleChairsForInfusionType(patient.infusionType, acuityTable)
	let bestOption = null

	for (const chairId of eligibleChairs) {
		const chair = find(schedule.chairSchedule, { chairId })
		if (!chair) {
			throw new ChairNotFoundError(chairId, "Today's schedule", schedule.chairs)
		}

		const overlappingPatientIds = chairsWithFutureAssignments.getOverlappingPatientsOnChair(
			chair,
			patient,
			sandbox,
			isTomorrowMode
		)
		if (overlappingPatientIds.length === 0) {
			return { chairId, otherPatientsToReassign: [] }
		}

		const blocked = overlappingPatientIds.some(patientId => {
			const other = find(schedule.patientSchedule, { patientId })
			if (isSmallChairPatient(other, acuityTable)) return true
			if (other.internalStatus === "Ongoing") return true
			return false
		})
		if (blocked) continue

		if (
			bestOption === null ||
			overlappingPatientIds.length < bestOption.otherPatientsToReassign.length
		) {
			bestOption = { chairId, otherPatientsToReassign: overlappingPatientIds }
		}
	}
	if (bestOption === null) {
		throw new NoChairFoundForPatientError(
			`No chair found for small chair patient with id ${patient.patientId}`,
			patient.patientId
		)
	}
	return bestOption
}

const getChairForPatient = (patient, chairsWithFutureAssignments, sandbox, isTomorrowMode) =>
	isSmallChairPatient(patient, chairsWithFutureAssignments.acuityTable)
		? getChairForSmallChairPatient(patient, chairsWithFutureAssignments, sandbox, isTomorrowMode)
		: getChairForNonSmallChairPatient(patient, chairsWithFutureAssignments, sandbox, isTomorrowMode)

/**
 * Assigns each add-on patient a nurse and a chair.
 *
 * Nurse assignment goes through the unified reassignPatients algorithm
 * (allows bumping). Chair assignment uses the chair-specific eligibility
 * helpers and may bump non-ongoing patients off chairs.
 *
 * @param {{patientSchedule, nurseSchedule, chairSchedule}} schedule
 * @param {Object} acuityTable
 * @param {Array<Patient>} unassignedPatients
 * @param {Object} settings - includes maxAcuityForNurse, maxPatientsForNurse,
 *   oneToOneAcuityLevel
 * @param {boolean} sandbox
 * @param {Object} logging
 * @param {boolean} isTomorrowMode
 * @returns {{reassignments: Array<{patientId, newNurseId, newChairId}>, unassignedPatients: Array}}
 */
const getAssignmentsForAddOnPatients = (
	schedule,
	acuityTable,
	unassignedPatients,
	settings,
	sandbox,
	logging,
	isTomorrowMode
) => {
	// Step 1: nurse assignment via unified algorithm.
	const nurseAssignments = reassignPatients({
		patientsToMove: unassignedPatients,
		candidateNurses: schedule.nurseSchedule,
		schedule,
		settings,
		sandbox,
		isTomorrowMode,
		allowBumping: true
	})

	// Step 2: build a working schedule reflecting the nurse decisions so chair
	// logic sees consistent state.
	const workingPatientSchedule = schedule.patientSchedule.map(p => ({ ...p }))
	for (const a of nurseAssignments) {
		const wp = workingPatientSchedule.find(p => p.patientId === a.patientId)
		if (wp) wp.assignedNurse = a.newNurseId
	}

	const chairsWithFutureAssignments = new ChairsWithFutureAssignments(
		{ ...schedule, patientSchedule: workingPatientSchedule },
		acuityTable
	)

	// Step 3: chair assignment, processed as a queue so chair-side bumps can
	// re-enter and get a new chair.
	const chairAssignments = new Map() // patientId -> chairId
	const chairQueue = unassignedPatients
		.filter(p => p.assignedChair == null)
		.map(p => ({ ...p }))

	while (chairQueue.length > 0) {
		const patient = chairQueue.shift()
		if (chairAssignments.has(patient.patientId)) continue

		try {
			const { chairId, otherPatientsToReassign } = getChairForPatient(
				patient,
				chairsWithFutureAssignments,
				sandbox,
				isTomorrowMode
			)

			for (const otherPatientId of otherPatientsToReassign) {
				const other = find(workingPatientSchedule, { patientId: otherPatientId })
				if (!other) {
					throw new PatientNotFoundError(
						otherPatientId,
						"Today's schedule",
						workingPatientSchedule
					)
				}
				other.assignedChair = null
				chairsWithFutureAssignments.removePatientFromChair(otherPatientId, chairId)
				chairAssignments.delete(otherPatientId)
				chairQueue.push({ ...other })
			}

			chairAssignments.set(patient.patientId, chairId)
			chairsWithFutureAssignments.addPatientToChair(patient.patientId, chairId)
		} catch (error) {
			logging.warn("getAssignmentsForAddOnPatients - couldn't assign chair", {
				patientId: patient.patientId,
				error: error.message
			})
		}
	}

	// Step 4: combine nurse and chair decisions. A patient must end up with
	// both a nurse and a chair — partial assignments violate the schedule
	// invariant. If we couldn't place either side, leave the patient in the
	// unassigned list for the user to handle manually.
	const reassignments = unassignedPatients
		.map(p => {
			const nurseAssignment = nurseAssignments.find(a => a.patientId === p.patientId)
			const chairId = chairAssignments.get(p.patientId) ?? p.assignedChair ?? null
			const newNurseId = nurseAssignment?.newNurseId ?? p.assignedNurse ?? null
			if (newNurseId == null || chairId == null) return null
			return { patientId: p.patientId, newNurseId, newChairId: chairId }
		})
		.filter(Boolean)

	return { reassignments, unassignedPatients }
}

export default getAssignmentsForAddOnPatients
